import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { CompassIcon, HowSectionListIcon } from '~/features/enterprise/landing-pages/icons';

export type HowConfig = {
  steps: { id: string }[];
  /** Eyebrow icon; `list-steps` matches PBL Figma HOW frame (vs default compass). */
  headerIconId?: 'compass' | 'list-steps';
};

export function HowSection({ config, slug }: { config: HowConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.how`;
  const eyebrowIconProps = { color: 'grey.500' as const, height: '18px', width: '18px' };
  const headerIcon =
    config.headerIconId === 'list-steps' ? (
      <HowSectionListIcon {...eyebrowIconProps} />
    ) : (
      <CompassIcon {...eyebrowIconProps} />
    );

  return (
    <Box as='section' bg='#f9f9f9' py={{ base: '48px', md: '64px' }}>
      <ContentContainer
        contentMaxWidth='content-max-width-md'
        display='flex'
        flexDirection='column'
        gap={{ base: '32px', md: '40px' }}
      >
        <SectionHeader
          color='grey.500'
          iconSlot={headerIcon}
          subtitle={t(`${tPrefix}.subtitle`) as string}
          descriptionSlot={
            <Text color='grey.500' margin='0 auto' maxWidth='843px'>
              {t(`${tPrefix}.description`)}
            </Text>
          }
          titleSlot={
            <Heading
              as='h2'
              color='grey.900'
              fontSize={{ base: '2.25rem', md: '3.25rem' }}
              fontWeight='600'
              letterSpacing='-2px'
              lineHeight='1.2'
            >
              {t(`${tPrefix}.title`)}
            </Heading>
          }
        />

        <Flex direction={{ base: 'column', md: 'row' }} gap='16px'>
          {config.steps.map((step, index) => (
            <Flex
              key={step.id}
              bg='white'
              borderRadius='8px'
              direction='column'
              flex={{ md: '1' }}
              gap='24px'
              padding='29px'
            >
              <Flex
                alignItems='center'
                bg='rgba(123, 162, 255, 0.15)'
                borderRadius='4px'
                height='fit-content'
                justifyContent='center'
                px='14px'
                py='8px'
                width='fit-content'
              >
                <Text color='blue.500' fontFamily='heading' fontSize='1.25rem' fontWeight='800' lineHeight='1.2'>
                  {index + 1}
                </Text>
              </Flex>
              <Text color='black' fontFamily='heading' fontSize='1.25rem' fontWeight='600' lineHeight='1.2'>
                {t(`${tPrefix}.steps.${step.id}.title`)}
              </Text>
              <Text color='grey.500' fontSize='0.9375rem' fontWeight='500' lineHeight='1.4'>
                {t(`${tPrefix}.steps.${step.id}.description`)}
              </Text>
            </Flex>
          ))}
        </Flex>
      </ContentContainer>
    </Box>
  );
}
